/**
 * Балансы по биржам — отдельная страница из меню.
 *
 * Панель та же, что в портфеле (`BalancesPanel`), но здесь она главная, а не
 * справочная строка под сделками. Два добавления ради «актуальности»:
 *   - возраст цифр: бэкенд опрашивает биржи раз в минуту, и без отметки
 *     времени нельзя понять, смотришь ты на свежее или на минуту назад;
 *   - кнопка «Опросить биржи»: `/api/balances/refresh` идёт к биржам прямо
 *     сейчас — перед заливкой ждать минутный цикл не хочется.
 * Плюс переключатель «все активы»: для спот-ноги важна сама монета на споте
 * (AZTEC на Hyperliquid), а по умолчанию панель показывает только деньги.
 *
 * 16.10 — в стиле терминала: шапка-панель со сводкой плитками (всего,
 * свободно, бирж, крупнейшая), карточки бирж ниже.
 */
import { useSecondTick } from '../utils/secondTick';
import React, { useState } from 'react';
import { AlertCircle, Building2, Coins, Crown, RefreshCw, Unlock, Wallet } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { apiClient } from '../api/client';
import { BalancesPanel, type BalancesSummary } from '../components/BalancesPanel';
import { exchangeLabel } from '../components/ExchangeIcon';
import { Switch } from '../ui/Switch';
import { fmtUsd } from '../utils/money';

const ago = (ts: number | null, now: number): string => {
  if (!ts) return i18n.t('balances:ago.never');
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 5) return i18n.t('balances:ago.justNow');
  if (s < 60) return i18n.t('balances:ago.sec', { n: s });
  const m = Math.floor(s / 60);
  return m < 60 ? i18n.t('balances:ago.min', { n: m }) : i18n.t('balances:ago.hours', { n: Math.floor(m / 60) });
};

/** «Опрос бирж: N с назад» — тикает только эта строка (общий секундный тик), а
 *  не вся страница с балансами. */
const PolledAgo: React.FC<{ ts: number | null }> = ({ ts }) => {
  const { t } = useTranslation('balances');
  useSecondTick();
  return <>{t('polledShort', { ago: ago(ts, Date.now()) })}</>;
};

/** Плитка сводки (.sb-kpi) — как на фандинге и фермах. */
const Kpi: React.FC<{
  icon: React.ReactNode; label: string; value: React.ReactNode; hint?: React.ReactNode; title?: string;
  tone?: 'green' | 'red' | 'muted' | 'accent'; className?: string;
}> = ({ icon, label, value, hint, title, tone, className }) => (
  <div title={title} data-tone={tone} className={clsx('sb-kpi', className)}>
    <div className="sb-kpi-label">{icon}<span className="truncate">{label}</span></div>
    <div className={clsx('sb-kpi-value', tone === 'green' ? 'text-trade-green' : tone === 'red' ? 'text-trade-red' : 'text-foreground')}>{value}</div>
    {hint != null && <div className="truncate text-[11px] text-muted-foreground">{hint}</div>}
  </div>
);

const BalancesPage: React.FC = () => {
  const { t } = useTranslation('balances');
  const [allAssets, setAllAssets] = useState(false);
  const [token, setToken] = useState(0);
  const [ts, setTs] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sum, setSum] = useState<BalancesSummary | null>(null);

  async function refresh() {
    setBusy(true); setErr(null);
    try {
      const r = await apiClient.raw.post('/api/balances/refresh');
      setTs(Number(r.data?.ts) || Date.now());
      setToken(t => t + 1);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    } finally { setBusy(false); }
  }

  const freePct = sum && sum.total > 0 ? Math.min(100, Math.max(0, (sum.free / sum.total) * 100)) : null;

  return (
    <div className="mx-auto max-w-[1600px] space-y-3 px-3 py-4 text-foreground sm:px-6 sm:py-5 lg:px-8">
      {/* Шапка — панель терминала: название, возраст цифр, действия, сводка. */}
      <div className="sb-panel !overflow-visible px-3 py-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="sb-badgeicon"><Wallet size={16} /></span>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold tracking-tight">{t('title')}</h1>
            <p className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground" title={t('autoNote')}>
              <span className="sb-live" /> <PolledAgo ts={ts} />
            </p>
          </div>
          {/* Высота 40px у тумблера и кнопки — под палец: на телефоне в маленький
              квадратик попадали через раз. */}
          <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end">
            <label className="inline-flex h-10 cursor-pointer select-none items-center gap-2 text-[13px] text-muted-foreground"
                   htmlFor="bal-all-assets">
              <Switch id="bal-all-assets" on={allAssets} onChange={setAllAssets} label={t('allAssets')} />
              <span>{t('allAssetsShort')}</span>
            </label>
            <button type="button" onClick={refresh} disabled={busy}
              className={clsx(
                'sb-press inline-flex h-10 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] font-medium transition-colors sm:h-9',
                busy ? 'border-primary/50 bg-primary/10 text-foreground' : 'border-border bg-background/50 hover:border-primary/50',
              )}>
              <RefreshCw size={14} className={clsx(busy && 'animate-spin')} />
              {busy ? t('refreshing') : t('refresh')}
            </button>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
          <Kpi icon={<Coins size={12} />} label={t('kpi.total')} tone={sum ? 'green' : 'muted'}
               value={sum ? fmtUsd(sum.total) : '—'} hint={t('kpi.totalHint')} />
          <Kpi icon={<Unlock size={12} />} label={t('kpi.free')} tone="muted"
               value={sum ? fmtUsd(sum.free) : '—'}
               hint={freePct != null ? t('kpi.freeHint', { pct: freePct.toFixed(0) }) : undefined} />
          <Kpi icon={<Building2 size={12} />} label={t('kpi.venues')} tone="muted"
               value={sum ? String(sum.venues) : '—'} />
          <Kpi icon={<Crown size={12} />} label={t('kpi.top')} tone={sum?.top ? 'accent' : 'muted'}
               value={sum?.top ? exchangeLabel(sum.top.exchange) : '—'}
               hint={sum?.top ? fmtUsd(sum.top.total) : undefined} />
        </div>
      </div>

      {err && (
        <div className="sb-pop flex items-start gap-2 rounded-xl border border-trade-red/40 bg-trade-red/10 px-3 py-2.5 text-[13px] text-trade-red">
          <AlertCircle size={15} className="mt-0.5 shrink-0" /> <span className="break-words">{err}</span>
        </div>
      )}

      <div className="sb-panel sb-panel-2 p-2.5 sm:p-3">
        <BalancesPanel allAssets={allAssets} refreshToken={token} onLoaded={setTs} onSummary={setSum} />
      </div>
    </div>
  );
};

export default BalancesPage;
